import { DEFAULT_TRIGGER_EVENT_NAME, NODE_TYPES } from 'modules/workflows/constants';
import { WorkflowType } from 'modules/workflows/enums';
// biome-ignore lint/correctness/noUnusedImports: this is a JS file, so the type is unused in the code. But we're using it in a JSDOC comment, which TS can use to extrapulate the type.
import { AUTH_TOKEN_FIELD } from 'modules/workflowNodeEditors/components/CallWebhookEditor/CallWebhookEditor.constants';
import { SECURE_CREDENTIAL_VALUE } from 'modules/workflowWebhooks/constants';
import { constructChannels, fieldsMatchUtils, parseNodeIdToInt, sendNodeUtils } from '../nodeUtils';

/**
 * Updates the fields as follows:
 * 1. Removes node fields that are not needed for saving on the frontend, but
 *    are needed for saving on the backend
 *    https://iterable.slack.com/archives/CN6649E9L/p1647373836232349å
 * 2. Parses fields from a frontend readable format to a backend savable format
 */
const updateNodeFields = (initialNode) => {
  const {
    canClone,
    canDelete,
    comboId,
    defaultTitle,
    depth,
    hasError,
    isNote,
    isAIGenerated,
    visitorsExitedCount,
    visitorsEnteredCount,
    edgeVisitorPercentages,
    showVisitorCounts,
    showNumUsersCount,
    isInProgressTotalPublishedView,
    isSelected,
    parentId,
    resource,
    resourceName,
    resources,
    style,
    type,
    clonedNode,
    ...node
  } = initialNode ?? {};
  const {
    __typename,
    childNodes = [],
    nodeAnalytics,
    resource: nodeDataResource,
    selectedEditor,
    textSegments,
    authTokenDisplay,
    ...nodeData
  } = initialNode?.nodeData ?? {};

  const isCHONode = type === NODE_TYPES.CHANNEL_OPT;

  if (isCHONode) {
    // Converts flowchart child nodes to channels for backend
    nodeData.channels = constructChannels(childNodes);
  }

  if (type === NODE_TYPES.FIELDS_MATCH) {
    nodeData.searchQuery = fieldsMatchUtils.parseSearchQueryForSaving(nodeData.searchQuery);
  }

  const parsedNode = {
    ...(node || {}),
    // This causes errors for non-CHO nodes
    ...(isCHONode && clonedNode && { clonedNode }),
  };

  const isAuthTokenDisplayPresent = !!initialNode?.nodeData
    ? AUTH_TOKEN_FIELD in initialNode.nodeData
    : false;
  const isAuthTokenUpdated =
    type === NODE_TYPES.CALL_WEBHOOK &&
    isAuthTokenDisplayPresent &&
    authTokenDisplay !== SECURE_CREDENTIAL_VALUE;

  const updatedNodeData = isAuthTokenUpdated
    ? { ...nodeData, authToken: authTokenDisplay }
    : nodeData;

  return {
    ...parsedNode,
    nodeData: updatedNodeData,
  };
};

/**
 * Basic set of data conversions required for all nodes by backend when saving
 */
export const parseNodeForSaving = (node = {}, workflowId) => {
  return {
    ...updateNodeFields(node),
    title: node.title ?? node.defaultTitle,
    id: parseNodeIdToInt(node.id),
    /* temporary fix: y-coordinate seems to be a float, not an integer
backend expects an integer */
    x: Math.round(node.x),
    y: Math.round(node.y),
    liveJourneyNodeId: node?.liveWorkflowNodeId || null, // this is a new property that the backend will be expecting on nodes for certain action
    workflowId,
  };
};

/**
 * New send nodes without any campaign configs added should
 * have campaigns created with default titles on workflow save
 *
 * These nodes are identified with a negative id and send node type
 */
const parseCampaignMetadata = ({ node, workflow }) => {
  const { campaignMetadata = {}, projectId } = workflow;
  const { nodeData, id } = node;
  const campaign = {
    customConversion: null,
    labels: [],
    name: null,
    useCustomConversionMetric: false,
    ...campaignMetadata[id],
    ...(nodeData?.resource?.campaign || {}),
    id: null,
  };

  return {
    ...campaign,
    useCustomConversionMetric: sendNodeUtils.shouldUseCustomConversionMetric(
      campaign.customConversion,
    ),
    labels: campaign.labels.map((l) => ({ ...l, projectId })),
  };
};

export const parseEdgeForSaving = (edge = {}, { workflowId, startNodeId } = {}) => {
  const srcWorkflowNodeId = parseInt(edge.source, 10);
  const isStartNode = srcWorkflowNodeId === startNodeId;

  // Every node except for the start node has one input connector, so the anchor index will be
  // off by 1
  let srcWorkflowNodeOutputIndex = isStartNode ? 0 : edge.sourceAnchor - 1;

  // Can never have negatives.  This shouldn't happen, but safety measure just in case.
  if (srcWorkflowNodeOutputIndex < 0) {
    srcWorkflowNodeOutputIndex = 0;
  }
  return {
    workflowId,
    srcWorkflowNodeId,
    destWorkflowNodeId: parseInt(edge.target, 10),
    srcWorkflowNodeOutputIndex,
  };
};

export const parseWorkflowForSaving = (workflow = {}) => {
  const { id: workflowId } = workflow;

  const { parsedNodes, parsedNotes, newCampaigns, startNode } = (workflow.nodes || []).reduce(
    (acc, node) => {
      const { newCampaigns: accCampaigns } = acc;
      const { id, type, isNote = false } = node;
      const nodeToSave = parseNodeForSaving(node, workflow.id);
      // edge case, in case a node is created without a node class
      // we should not be able to save these
      const isMissingNodeClass = !nodeToSave.nodeData?.nodeClass;

      if (id < 0 && sendNodeUtils.isSendNode(type)) {
        // updates campaign metadata field in workflow
        // doesn't actually change node
        accCampaigns[id] = parseCampaignMetadata({ node, workflow });
      }

      return {
        parsedNodes:
          isNote || isMissingNodeClass ? acc.parsedNodes : [...acc.parsedNodes, nodeToSave],
        parsedNotes: isNote ? [...acc.parsedNotes, nodeToSave] : acc.parsedNotes,
        newCampaigns: accCampaigns,
        startNode: type === NODE_TYPES.START ? nodeToSave : acc.startNode,
      };
    },
    { parsedNodes: [], parsedNotes: [], newCampaigns: {}, startNode: {} },
  );

  /**
   * parseGoals
   * @param {BackendJourneyConversions} goals
   * @returns
   */
  const parseGoals = (goals) => {
    return (goals || []).map((goal) => {
      return goal?.conversions?.map((conversion) => ({
        attributionMode: conversion?.attributionMode,
        attributionPeriodHours: conversion?.attributionPeriodHours,
        eventName: conversion?.eventName,
      }));
    });
  };

  const parsedEdges = (workflow.edges || []).map((edge) =>
    parseEdgeForSaving(edge, { workflowId, startNodeId: startNode?.id }),
  );

  return {
    triggerEventNames: [DEFAULT_TRIGGER_EVENT_NAME], // overidden if trigger selected from start node
    ...workflow,
    ...(workflow.goals && { goals: parseGoals(workflow?.goals) }),
    // this is a new property that the backend will be expecting on workflows for certain actions
    publishedJourneyId:
      workflow?.workflowReference?.workflowType === WorkflowType.PUBLISHED
        ? workflow?.workflowReference?.id
        : null,
    edges: parsedEdges,
    nodes: parsedNodes,
    notes: parsedNotes,
    campaignMetadata: { ...workflow.campaignMetadata, ...newCampaigns },
  };
};

/**
 * When campaign metadata is empty, existing saved
 * campaign data will be used to populate the
 * published journey. Workflow validation guarantees
 * that campaigns in drafts are either fully created or
 * updated before publishing is enacted.
 *
 * Long story short, some inconsistent data we are
 * currently returned and reading back in from
 * /upsertWorkflowCampaign was causing some campaign
 * values to be overwritten when attempting to send a
 * copy of the current metadata back during publish.
 *
 * Returns a copy of the provided workflow with the
 * campaignMetadata field emptied out.
 */
export const saveWorkflowWithoutCampaignMetadata = (workflow = {}) => ({
  ...parseWorkflowForSaving(workflow),
  campaignMetadata: {},
});

export const parseAddedNodes = (nodes = []) =>
  nodes.map(({ data }) =>
    data.type === NODE_TYPES.NOTE ? { ...data, isInlineEditInProgress: true } : data,
  );

const removeCampaignFromNodeData = (nodeData = {}) => ({
  ...nodeData,
  nodeAnalytics: null,
  textSegments: {},
  campaignId: null,
});

const removeCampaignFromCHONode = (nodeData = {}) => {
  const { childNodes = [], channels = [] } = nodeData;

  return {
    ...nodeData,
    childNodes: childNodes.map((childNode) => ({
      ...childNode,
      nodeData: removeCampaignFromNodeData(childNode.nodeData),
    })),
    channels: channels.map((channel = {}) => {
      const { value = {} } = channel;
      return { ...channel, value: { ...value, campaignId: null } };
    }),
  };
};

const getParsedSendNodeCloneProps = ({ isTileset, node }) => {
  if (isTileset) return {};
  const isCHONode = node.type === NODE_TYPES.CHANNEL_OPT;
  return isCHONode
    ? { clonedFromId: parseNodeIdToInt(node?.id) }
    : { cloneCampaignOfId: node?.nodeData?.campaignId };
};

// for send nodes, clear campaign reference and associated analytics
const parseSendNodeCopy = (node, isTileset) => {
  const isCHONode = node.type === NODE_TYPES.CHANNEL_OPT;
  const nodeData = isCHONode
    ? removeCampaignFromCHONode(node.nodeData)
    : removeCampaignFromNodeData(node.nodeData);

  return {
    ...node,
    ...getParsedSendNodeCloneProps({ isTileset, node }),
    nodeData,
  };
};

export const parseClonedNodes = (nodes = [], isTileset = false) =>
  nodes.map((node) => {
    const { type } = node;

    if (type === NODE_TYPES.NOTE) return node;

    const updatedNode = {
      ...node,
      numUsers: 0,
      generatedWithAI: undefined,
      visitorsExitedCount: 0,
      visitorsEnteredCount: 0,
      edgeVisitorPercentages: {},
    };

    if (!sendNodeUtils.isSendNode(type)) return updatedNode;

    return parseSendNodeCopy(updatedNode, isTileset);
  });

export const parseAddedEdges = (edges) =>
  edges.reduce((acc, { data }) => {
    const { source, sourceAnchor, target, targetNode } = data;
    // do not allow another node to connect to the start node
    return targetNode?.getModel()?.type === NODE_TYPES.START
      ? acc
      : // always connect to anchor 0 (in anchor) in the target node
        [...acc, { source, sourceAnchor, target, targetAnchor: 0 }];
  }, []);
